import { useEffect } from "react";

/**
 * Client-side SEO injector. No new dependencies.
 *
 * Manages: <title>, <meta name="description">, <link rel="canonical">,
 * Open Graph + Twitter cards, and JSON-LD structured data. Cleans up on
 * unmount so each route owns its own head state.
 *
 * Crawlers (Google, Bing, Yandex, DuckDuckGo) all run JS now — client-side
 * injection is fully indexable. For absolute maximum coverage we could move
 * to SSR later, but this is sufficient until ~10k topics.
 */
const HEAD_FLAG = "data-tcp-seo";

const SITE_NAME = "The Context Project";
const SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin;

function clearOurTags() {
    // Only clear injected JSON-LD scripts — meta tags and canonical link
    // are now updated in-place (see upsertMeta / upsertCanonical) so they
    // never accumulate. Removing meta tags here would re-introduce the
    // duplicate-tag bug for social crawlers that read the static defaults.
    document.querySelectorAll(`script[type="application/ld+json"][${HEAD_FLAG}]`).forEach((el) => el.remove());
}

function setTitle(title) {
    if (title) document.title = title;
}

function upsertMeta(attr, key, content) {
    if (!content) return;
    // Find ANY existing tag for this attr/key — including static defaults
    // in index.html — so we don't end up with duplicate og:url / og:image
    // tags after client-side hydration.
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    el.setAttribute(HEAD_FLAG, "1");
}

function upsertCanonical(href) {
    if (!href) return;
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    el.setAttribute(HEAD_FLAG, "1");
}

function injectJsonLd(data) {
    if (!data) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(HEAD_FLAG, "1");
    script.text = JSON.stringify(data, null, 0);
    document.head.appendChild(script);
}

export default function SEO({
    title,
    description,
    canonicalPath,
    image,
    type = "website",
    jsonLd,
    noindex = false,
}) {
    useEffect(() => {
        clearOurTags();
        const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
        setTitle(fullTitle);
        const url = canonicalPath ? `${SITE_URL}${canonicalPath}` : SITE_URL;
        const img = image || `${SITE_URL}/og-default.jpg`;

        upsertMeta("name", "description", description);
        upsertCanonical(url);

        // Mobile / browser chrome polish
        upsertMeta("name", "theme-color", "#0B1220");
        upsertMeta("name", "application-name", SITE_NAME);
        upsertMeta("name", "apple-mobile-web-app-title", SITE_NAME);

        if (noindex) {
            upsertMeta("name", "robots", "noindex, nofollow");
        }

        // Open Graph
        upsertMeta("property", "og:title", fullTitle);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:type", type);
        upsertMeta("property", "og:url", url);
        upsertMeta("property", "og:site_name", SITE_NAME);
        upsertMeta("property", "og:image", img);
        upsertMeta("property", "og:image:width", "1200");
        upsertMeta("property", "og:image:height", "630");
        upsertMeta("property", "og:image:alt", title ? `${title} — The Context Project` : "The Context Project — Context before conclusions");

        // Twitter
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", fullTitle);
        upsertMeta("name", "twitter:description", description);
        upsertMeta("name", "twitter:image", img);

        if (jsonLd) {
            const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            items.forEach(injectJsonLd);
        }

        return () => {
            // Don't clear on unmount — the next page's SEO clears + sets in its own effect.
            // Clearing here would race with the next page's hydration and cause a flicker
            // in tab title / share previews. The replacement model is correct.
        };
    }, [title, description, canonicalPath, image, type, jsonLd, noindex]);
    return null;
}

// ----- Convenience builders for our specific data shapes -----

export function topicJsonLd(topic, site = SITE_URL) {
    if (!topic) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: topic.title,
        description: topic.subtitle || topic.summary,
        url: `${site}/topic/${topic.slug}`,
        image: topic.featured_image,
        datePublished: topic.created_at,
        dateModified: topic.last_updated || topic.created_at,
        author: { "@type": "Organization", name: SITE_NAME },
        publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: { "@type": "ImageObject", url: `${site}/og-default.jpg` },
        },
        articleSection: topic.category,
        keywords: (topic.tags || []).join(", "),
        about: (topic.entities || []).slice(0, 12).map((e) => ({
            "@type": e.type === "person" ? "Person" : "Organization",
            name: e.name,
        })),
    };
}

export function breadcrumbJsonLd(crumbs, site = SITE_URL) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: c.path ? `${site}${c.path}` : undefined,
        })),
    };
}

export function entityJsonLd(entity, site = SITE_URL) {
    if (!entity) return null;
    const type = entity.type === "person" ? "Person" : "Organization";
    return {
        "@context": "https://schema.org",
        "@type": type,
        name: entity.name,
        description: entity.bio || entity.role,
        url: `${site}/entity/${entity.slug}`,
        sameAs: entity.url ? [entity.url] : undefined,
    };
}

export function websiteJsonLd(site = SITE_URL) {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: site,
        potentialAction: {
            "@type": "SearchAction",
            target: `${site}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    };
}
